import noop from 'lodash/noop';
import React, { useState } from 'react';
import { ConfigurationCanvasProps } from './types';
import { CanvasEvent } from '../../sweep-canvas/canvas-types';
import { CanvasNodesMouseMove } from '../../sweep-canvas/useCanvasNodesMouseMove';
import { ReserveSpaceForPanelsProvider } from './panels-reserve-space/ReserveSpaceForPanelsCtx';
import { usedFunnelMapCrmOrgIds } from './utils/listFunnelMapCrmOrgIds';

const notSetFunnelMap: FunnelMap = {
  id: 'NOT_SET',
  funnels: {},
  recordTypes: {},
  name: 'NOT_SET',
  isPinned: false,
  createdAt: 'NOT_SET',
  createdById: 'NOT_SET',
  updatedAt: 'NOT_SET',
  updatedById: 'NOT_SET',
  funnelsData: {},
  recordTypesData: {},
  hubspotFunnels: {},
  hubspotFunnelsData: {},
  thirdPartyFunnels: {},
  thirdPartyFunnelsData: {},
  metadataElements: {},
  metadataElementsData: {},
  version: 0,
};

export interface ConfigurationCanvasContextType {
  setConfigurationCanvasProps: (props: ConfigurationCanvasProps) => void;
  configurationCanvasProps: ConfigurationCanvasProps;

  /**
   * The CRM org ID of the canvas.
   * This is what is used by the canvas itself
   * */
  canvasCrmOrgId: string;

  lastAttemptedFunnelMapSave?: FunnelMap;
  setLastAttemptedFunnelMapSave: (funnelMap?: FunnelMap) => void;
  commentsOn: boolean;
  setCommentsOn: (commentsOn: boolean) => void;
  canvasFunnelMap: FunnelMap;
  setCanvasFunnelMap: (funnelMap: FunnelMap) => void;
  onSweepElementsChange: (events: CanvasEvent[]) => void;
  canvasNodesMouseMove?: CanvasNodesMouseMove;
  usedCrmOrgIds: string[];
}

export const ConfigurationCanvasContext = React.createContext<ConfigurationCanvasContextType>({
  setConfigurationCanvasProps: noop,
  configurationCanvasProps: {
    onStageGateClick: noop,
    visibilityElements: {
      type: 'map',
      visibilityMap: {},
    },
  },
  canvasCrmOrgId: 'NOT_SET',
  setLastAttemptedFunnelMapSave: noop,
  setCommentsOn: noop,
  commentsOn: false,
  canvasFunnelMap: { ...notSetFunnelMap },
  setCanvasFunnelMap: noop,
  onSweepElementsChange: noop,
  usedCrmOrgIds: ['NOT_SET'],
});

export const ConfigurationCanvasProvider = ({
  children,
  canvasCrmOrgId,
  canvasFunnelMap,
  setCanvasFunnelMap,
  onSweepElementsChange,
}: {
  children: React.ReactNode;
  canvasCrmOrgId: string;
  canvasFunnelMap: FunnelMap;
  setCanvasFunnelMap: (funnelMap: FunnelMap) => void;
  onSweepElementsChange: (event: CanvasEvent[]) => void;
}) => {
  const [lastAttemptedFunnelMapSave, setLastAttemptedFunnelMapSave] = useState<FunnelMap>();

  const [commentsOn, setCommentsOn] = useState(false);

  const [configurationCanvasProps, setConfigurationCanvasProps] =
    useState<ConfigurationCanvasProps>({
      onStageGateClick: noop,
      visibilityElements: {
        type: 'map',
        visibilityMap: {},
      },
    });

  return (
    <ReserveSpaceForPanelsProvider>
      <ConfigurationCanvasContext.Provider
        value={{
          setConfigurationCanvasProps,
          configurationCanvasProps,
          canvasCrmOrgId,
          lastAttemptedFunnelMapSave,
          setLastAttemptedFunnelMapSave,
          commentsOn,
          setCommentsOn,
          canvasFunnelMap,
          setCanvasFunnelMap,
          onSweepElementsChange,
          usedCrmOrgIds: usedFunnelMapCrmOrgIds(canvasFunnelMap),
        }}
      >
        {children}
      </ConfigurationCanvasContext.Provider>
    </ReserveSpaceForPanelsProvider>
  );
};

export const useConfigurationCanvasContext = () => {
  const context = React.useContext(ConfigurationCanvasContext);
  if (!context) {
    throw new Error(
      'useConfigurationCanvasContext must be used within a ConfigurationCanvasProvider',
    );
  }
  return context;
};
